import React, { useCallback, useEffect, useRef, useState } from "react";
import { App } from "antd";
import axios from "axios";
import "../QRLink/QRLink.css";
import HeaderLayout from "../../components/Header";
import FooterLayout from "../../components/Footer";
import PageHeader from "../../components/PageHeader";
import QRTypeTabs from "../../components/QRTypeTabs";
import HtmlEditor from "../../components/HtmlEditor";
import { QRCodeCanvas } from "qrcode.react";
import {
  CheckCircleFilled,
  DownloadOutlined,
  QrcodeOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { DEFAULT_QR_COLOR, DEFAULT_QR_SIZE, QR_COLORS, QR_CONFIGS, QR_SIZES } from "../../config/qrFormConfig";
import { downloadCanvasPng, downloadQrAttachment, downloadRemoteFile, safeFileName } from "../../utils/downloadFile";

const stripHtml = (html = "") => html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
const BRAND_LOGO_TYPES = ["image/jpeg", "image/png"];
const BRAND_LOGO_EXTENSIONS = [".jpg", ".jpeg", ".png"];
const MESSAGE_IMAGE_TYPES = ["image/jpeg", "image/png"];
const MESSAGE_IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png"];
const DATE_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const isSupportedBrandLogo = (file) =>
  file &&
  (BRAND_LOGO_TYPES.includes(file.type) ||
    BRAND_LOGO_EXTENSIONS.some((extension) => file.name.toLowerCase().endsWith(extension)));

const isSupportedMessageImage = (file) =>
  file &&
  (MESSAGE_IMAGE_TYPES.includes(file.type) ||
    MESSAGE_IMAGE_EXTENSIONS.some((extension) => file.name.toLowerCase().endsWith(extension)));

const formatDisplayDate = (value = "") => {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return value;
  return `${day}-${DATE_MONTHS[Number(month) - 1]}-${year}`;
};

const parseDisplayDate = (value = "") => {
  const match = value.match(/^(\d{2})-([A-Za-z]{3})-(\d{4})$/);
  if (!match) return value;
  const monthIndex = DATE_MONTHS.findIndex((month) => month.toLowerCase() === match[2].toLowerCase());
  if (monthIndex < 0) return value;
  return `${match[3]}-${String(monthIndex + 1).padStart(2, "0")}-${match[1]}`;
};

const QRCreate = ({ qrType }) => {
  const config = QR_CONFIGS[qrType];
  const { message } = App.useApp();
  const [title, setTitle] = useState("");
  const [primaryValue, setPrimaryValue] = useState("");
  const [platform, setPlatform] = useState(config?.platformOptions?.[0] || "");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [payload, setPayload] = useState({});
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [qrColor, setQrColor] = useState(DEFAULT_QR_COLOR);
  const [qrSize, setQrSize] = useState(DEFAULT_QR_SIZE);
  const [loading, setLoading] = useState(false);
  const [qrResult, setQrResult] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState("");
  const canvasWrapRef = useRef(null);
  const isModule = window.location.pathname.includes("/dashboard/");

  const resetFormFields = useCallback(({ clearResult = true } = {}) => {
    setTitle("");
    setPrimaryValue("");
    setPlatform(config?.platformOptions?.[0] || "");
    setSubject("");
    setDescription("");
    setPayload({});
    setFile(null);
    setLogoPreviewUrl("");
    setFileInputKey((prev) => prev + 1);
    setPassword("");
    setConfirmPassword("");
    setQrColor(DEFAULT_QR_COLOR);
    setQrSize(DEFAULT_QR_SIZE);
    if (clearResult) {
      setQrResult(null);
    }
  }, [config]);

  useEffect(() => {
    resetFormFields();
  }, [qrType, resetFormFields]);

  useEffect(() => {
    if (qrType !== "Brand" || !file) {
      setLogoPreviewUrl("");
      return undefined;
    }

    let active = true;
    const reader = new FileReader();
    reader.onload = () => {
      if (active) {
        setLogoPreviewUrl(reader.result || "");
      }
    };
    reader.readAsDataURL(file);

    return () => {
      active = false;
    };
  }, [file, qrType]);

  if (!config) {
    return null;
  }

  const selectedSize = QR_SIZES.find((size) => size.value === qrSize)?.label || "Medium";

  const updatePayload = (key, value) => {
    setPayload((prev) => ({ ...prev, [key]: value }));
  };

  const handleFileSelect = (event) => {
    const nextFile = event.target.files?.[0] || null;
    if (qrType === "Brand" && nextFile && !isSupportedBrandLogo(nextFile)) {
      message.warning("Brand logo supports JPG, JPEG, and PNG only.");
      event.target.value = "";
      setFile(null);
      setLogoPreviewUrl("");
      return;
    }
    if (qrType === "Message" && nextFile && !isSupportedMessageImage(nextFile)) {
      message.warning("Message picture supports JPG, JPEG, and PNG only.");
      event.target.value = "";
      setFile(null);
      return;
    }

    setFile(nextFile);
  };

  const buildPayloadData = () => {
    const data = { ...payload };
    data.qrType = qrType;
    data.title = title;
    data.source = isModule ? "Web" : "Public-Web";
    if (config.platformOptions && platform) data.platform = platform;
    if (subject) data.subject = subject;
    if (stripHtml(description)) data[config.descriptionKey || "description"] = description;
    if (primaryValue && config.primaryKey) data[config.primaryKey] = primaryValue;
    return Object.fromEntries(
      Object.entries(data).filter(([, value]) => stripHtml(String(value || "")))
    );
  };

  const generateQR = async () => {
    if (!title.trim()) {
      message.warning("Please give your QR a name.");
      return;
    }
    if (config.primaryKey && !primaryValue.trim()) {
      message.warning(`Please enter ${config.primaryLabel.toLowerCase()}.`);
      return;
    }
    if (config.fileRequired && !file) {
      message.warning("Please upload a file.");
      return;
    }
    if (config.payloadFields) {
      const missingField = config.payloadFields.find(
        (field) => field.required && !String(payload[field.key] || "").trim()
      );
      if (missingField) {
        message.warning(`Please enter ${missingField.label.toLowerCase()}.`);
        return;
      }
    }
    if (qrType === "Brand" && file && !isSupportedBrandLogo(file)) {
      message.warning("Brand logo supports JPG, JPEG, and PNG only.");
      return;
    }
    if (qrType === "Message" && file && !isSupportedMessageImage(file)) {
      message.warning("Message picture supports JPG, JPEG, and PNG only.");
      return;
    }
    if (password && password.length < 6) {
      message.warning("Password must be at least 6 characters.");
      return;
    }
    if (password && password !== confirmPassword) {
      message.warning("Password and confirm password must match.");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("qrType", qrType);
      formData.append("title", title);
      formData.append("source", isModule ? "Web" : "Public-Web");
      formData.append("size", selectedSize);
      formData.append("color", qrColor);
      formData.append("backgroundColor", "#FFFFFF");

      if (config.primaryKey) formData.append(config.primaryKey, primaryValue);
      if (config.platformOptions && platform) formData.append("platform", platform);
      if (subject) formData.append("subject", subject);
      if (stripHtml(description)) formData.append(config.descriptionKey || "description", description);
      if (file && config.fileKey) formData.append(config.fileKey, file);
      if (password) {
        formData.append("password", password);
        formData.append("confirmPassword", confirmPassword);
      }

      const payloadData = buildPayloadData();
      if (Object.keys(payloadData).length > 0) {
        formData.append("payloadData", JSON.stringify(payloadData));
      }

      const token = localStorage.getItem("token") || "";
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/Qr/create`,
        formData,
        headers ? { headers } : undefined
      );

      if (response.data.error === false) {
        const data = response.data.data || {};
        setQrResult({
          id: data.id,
          qrValue: data.qrUrl || primaryValue || data.fileUrl || title,
          qrImageUrl: data.qrImageUrl || "",
          displayValue: data.link || data.qrText || data.fileUrl || primaryValue || stripHtml(description) || title,
          title: data.title || title,
          qrColor,
          qrSize,
          logoPreviewUrl: qrType === "Brand" ? logoPreviewUrl : "",
        });
        resetFormFields({ clearResult: false });
        message.success(response.data.message || "QR Code generated successfully");
      } else {
        message.error(response.data.message || "Failed to generate QR code.");
      }
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to generate QR code.");
    } finally {
      setLoading(false);
    }
  };

  const downloadQR = async () => {
    const fileTitle = qrResult?.title || title || "qr-code";

    if (qrResult?.id && localStorage.getItem("token")) {
      try {
        await downloadQrAttachment({
          baseUrl: process.env.REACT_APP_BASE_URL,
          id: qrResult.id,
          token: localStorage.getItem("token") || "",
          title: fileTitle,
          format: "png",
        });
        return;
      } catch {
        // Fall through to image/canvas download.
      }
    }

    if (qrResult?.qrImageUrl) {
      try {
        await downloadRemoteFile(qrResult.qrImageUrl, safeFileName(fileTitle, "png"));
      } catch {
        const canvas = canvasWrapRef.current?.querySelector("canvas");
        if (canvas) {
          downloadCanvasPng(canvas, safeFileName(fileTitle, "png"));
        } else {
          message.error("Unable to download QR image. Please try again.");
        }
      }
      return;
    }

    const canvas = canvasWrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const anchor = document.createElement("a");
    anchor.href = canvas.toDataURL("image/png");
    anchor.download = `${fileTitle.replace(/\s+/g, "-").toLowerCase() || "qr-code"}.png`;
    anchor.click();
  };

  const reset = () => {
    resetFormFields();
  };

  return (
    <>
      {isModule ? <PageHeader /> : <HeaderLayout />}
      <div className={`qrl-root ${isModule ? "qrl-module" : ""}`}>
        <div className="qrl-inner">
          <QRTypeTabs activeType={config.activeType} />

          <div className="qrl-form-side">
            <div className="qrl-heading">
              <div className="qrl-heading-icon"><QrcodeOutlined /></div>
              <div>
                <h1 className="qrl-title">{config.title}</h1>
                <p className="qrl-subtitle">{config.subtitle}</p>
              </div>
            </div>

            <div className="qrl-card">
              <div className="qrl-field">
                <label className="qrl-label">QR Code Name</label>
                <input className="qrl-input" placeholder="Enter QR name" value={title} onChange={(event) => setTitle(event.target.value)} />
              </div>

              {config.primaryKey && (
                <div className="qrl-field">
                  <label className="qrl-label">{config.primaryLabel}</label>
                  <input className="qrl-input" placeholder={config.primaryPlaceholder} value={primaryValue} onChange={(event) => setPrimaryValue(event.target.value)} />
                </div>
              )}

              {config.platformOptions && (
                <div className="qrl-field">
                  <label className="qrl-label">{config.platformLabel}</label>
                  <select className="qrl-input" value={platform} onChange={(event) => setPlatform(event.target.value)}>
                    {config.platformOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                </div>
              )}

              {config.subjectLabel && (
                <div className="qrl-field">
                  <label className="qrl-label">{config.subjectLabel}</label>
                  <input className="qrl-input" placeholder="You are invited" value={subject} onChange={(event) => setSubject(event.target.value)} />
                </div>
              )}

              {config.descriptionLabel && (
                <div className="qrl-field">
                  <label className="qrl-label">{config.descriptionLabel}</label>
                  <HtmlEditor value={description} onChange={setDescription} placeholder={qrType === "Text" ? "Write your text here" : "Write your message"} />
                </div>
              )}

              {config.payloadFields?.map((field) => (
                <div className="qrl-field" key={field.key}>
                  <label className="qrl-label">
                    {field.label}
                    {field.required && <span style={{ color: "#e5322d" }}> *</span>}
                  </label>
                  <input
                    className="qrl-input"
                    type={field.type || "text"}
                    placeholder={field.placeholder}
                    value={field.type === "date" ? parseDisplayDate(payload[field.key] || "") : payload[field.key] || ""}
                    onChange={(event) => updatePayload(field.key, field.type === "date" ? formatDisplayDate(event.target.value) : event.target.value)}
                    required={field.required}
                  />
                </div>
              ))}

              {config.fileKey && (
                <div className="qrl-field">
                  <label className="qrl-label">{config.fileLabel}</label>
                  <input key={fileInputKey} className="qrl-input" type="file" accept={config.fileAccept} onChange={handleFileSelect} />
                </div>
              )}

              <div className="qrl-divider" />

              <div className="qrl-field">
                <label className="qrl-label">QR Color</label>
                <div className="qrl-color-row">
                  {QR_COLORS.map((color) => (
                    <button key={color} className={`qrl-color-swatch ${qrColor === color ? "active" : ""}`} style={{ background: color }} onClick={() => setQrColor(color)} type="button" />
                  ))}
                  <input type="color" className="qrl-color-custom" value={qrColor} onChange={(event) => setQrColor(event.target.value)} />
                </div>
              </div>

              <div className="qrl-field">
                <label className="qrl-label">QR Size</label>
                <div className="qrl-size-row">
                  {QR_SIZES.map((size) => (
                    <button key={size.value} className={`qrl-size-btn ${qrSize === size.value ? "active" : ""}`} onClick={() => setQrSize(size.value)} type="button">
                      {size.label}<span>{size.value}px</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="qrl-field">
                <label className="qrl-label">Password Protection</label>
                <input className="qrl-input" type="password" placeholder="Optional password" value={password} onChange={(event) => setPassword(event.target.value)} />
              </div>
              {password && (
                <div className="qrl-field">
                  <label className="qrl-label">Confirm Password</label>
                  <input className="qrl-input" type="password" placeholder="Confirm password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} />
                </div>
              )}

              <div className="qrl-actions">
                <button className="qrl-generate-btn" onClick={generateQR} disabled={loading} type="button">
                  {loading ? <span className="qrl-spinner" /> : <QrcodeOutlined />}
                  {loading ? "Generating..." : "Generate QR Code"}
                </button>
                {qrResult && <button className="qrl-reset-btn" onClick={reset} type="button"><ReloadOutlined /> Start over</button>}
              </div>
            </div>
          </div>

          <div className="qrl-preview-side">
            <div className="qrl-preview-card">
              <div className="qrl-preview-label">QR Preview</div>
              {qrResult ? (
                <>
                  <div className="qrl-qr-wrap" ref={canvasWrapRef}>
                    {qrResult.qrImageUrl ? (
                      <>
                        <img src={qrResult.qrImageUrl} alt={qrResult.title} className="qrl-qr-image" />
                        <div style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", pointerEvents: "none" }}>
                          <QRCodeCanvas
                            value={qrResult.qrValue}
                            size={qrResult.qrSize || qrSize}
                            fgColor={qrResult.qrColor || qrColor}
                            bgColor="#ffffff"
                            level="H"
                            includeMargin={true}
                            imageSettings={qrResult.logoPreviewUrl ? {
                              src: qrResult.logoPreviewUrl,
                              width: Math.round((qrResult.qrSize || qrSize) * 0.18),
                              height: Math.round((qrResult.qrSize || qrSize) * 0.18),
                              excavate: true,
                            } : undefined}
                          />
                        </div>
                      </>
                    ) : (
                      <QRCodeCanvas
                        value={qrResult.qrValue}
                        size={qrResult.qrSize || qrSize}
                        fgColor={qrResult.qrColor || qrColor}
                        bgColor="#ffffff"
                        level="H"
                        includeMargin={true}
                        imageSettings={qrResult.logoPreviewUrl ? {
                          src: qrResult.logoPreviewUrl,
                          width: Math.round((qrResult.qrSize || qrSize) * 0.18),
                          height: Math.round((qrResult.qrSize || qrSize) * 0.18),
                          excavate: true,
                        } : undefined}
                      />
                    )}
                  </div>
                  <div className="qrl-preview-success"><CheckCircleFilled className="qrl-success-icon" /><span>QR code ready!</span></div>
                  <div className="qrl-preview-name">{qrResult.title}</div>
                  <div className="qrl-preview-url"><QrcodeOutlined /><span>{qrResult.displayValue.length > 40 ? qrResult.displayValue.slice(0, 40) + "..." : qrResult.displayValue}</span></div>
                  <button className="qrl-download-btn" onClick={downloadQR} type="button"><DownloadOutlined /> Download PNG</button>
                </>
              ) : (
                <div className="qrl-placeholder">
                  <div className="qrl-placeholder-icon"><QrcodeOutlined /></div>
                  <div className="qrl-placeholder-text">Fill in the form and click<br /><strong>Generate QR Code</strong></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {!isModule && <FooterLayout />}
    </>
  );
};

export default QRCreate;
